"use client";

import { ENERGY_WINDOW_STEP_SECONDS } from "@/constants/audioQuality";
import { ExtractedAudioSegment } from "@/types/defs/audioQuality/extractedAudioSegment";
import { extractAudioSegment } from "@/utils/audio/extractAudioSegment";

export const extractHighestEnergySegment = async ({
  audioBuffer,
  duration,
}: {
  audioBuffer: AudioBuffer;
  duration: number;
}): Promise<ExtractedAudioSegment> => {
  // If the source is shorter than the requested window, there is no segment to pick — return the full buffer as-is so callers don't get a buffer padded with undefined samples past the end of the audio.
  if (audioBuffer.duration <= duration) {
    return {
      audioBuffer,
      startSeconds: 0,
      endSeconds: audioBuffer.duration,
    };
  }

  const sampleRate = audioBuffer.sampleRate;
  const segmentLengthSamples = Math.floor(duration * sampleRate);
  const channelData = audioBuffer.getChannelData(0);

  // Build a squared-sample prefix sum once — O(n) — so each window's energy is a single subtraction instead of an inner loop, reducing the overall complexity from O(n × segmentLength) to O(n).
  const squaredSamplePrefixSums = new Float64Array(channelData.length + 1);
  for (let i = 0; i < channelData.length; i++) {
    squaredSamplePrefixSums[i + 1] =
      squaredSamplePrefixSums[i] + channelData[i] * channelData[i];
  }

  let maxEnergySum = 0;
  let peakSegmentStartSeconds = 0;
  const windowStepSamples = Math.floor(sampleRate * ENERGY_WINDOW_STEP_SECONDS);

  for (
    let i = 0;
    i <= channelData.length - segmentLengthSamples;
    i += windowStepSamples
  ) {
    const energySum =
      squaredSamplePrefixSums[i + segmentLengthSamples] -
      squaredSamplePrefixSums[i];
    if (energySum > maxEnergySum) {
      maxEnergySum = energySum;
      peakSegmentStartSeconds = i / sampleRate;
    }
  }

  const peakSegmentEndSeconds = peakSegmentStartSeconds + duration;

  return {
    audioBuffer: await extractAudioSegment({
      audioBuffer,
      startTimeSeconds: peakSegmentStartSeconds,
      endTimeSeconds: peakSegmentEndSeconds,
    }),
    startSeconds: peakSegmentStartSeconds,
    endSeconds: peakSegmentEndSeconds,
  };
};
